import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import useResizeObserver from '@react-hook/resize-observer';
import { ThreadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { AnimatePresence, motion } from 'motion/react';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import {
  CHAT_GAP,
  chatIsAvatarVariant,
  HEADER_HEIGHT,
  MiniChatContext,
  moveChat as getDestinationOrder,
  useMiniChatStore,
  WIDTHS,
} from '@frontend/mini-chat-store';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { useAlert } from '@frontend/design-system';
import { MiniChat } from './mini-chat';
import { MiniChatHeader } from './mini-chat-header';
import { OverflowChatsButton } from './overflow-chats';
import { SizeVariant } from './types';

const INITIAL_RIGHT = 57; // This is the assumed action-bar width, which is rendered to the right of the component

export const MiniChatManager = () => {
  const { t } = useTranslation('mini-chat');
  const alert = useAlert();
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useAppFlagStore();
  const aclEnforcementEnabled = getFeatureFlagValue({
    locationIds: selectedLocationIds,
    flagName: 'nwx:messaging-inbox-fe-acl-enforcement',
    strategy: 'ALL',
  });
  const locationIdsWithACL = aclEnforcementEnabled
    ? filterLocationIdsByACLs({
        locationIds: selectedLocationIds,
        acls: Permission.SMS_INBOX_MANAGE,
      })
    : selectedLocationIds;

  const {
    chats,
    rolledUp,
    openChat,
    minimizeChat,
    setAvatarChatCount,
    avatarChatCount,
    removeChat,
    removeChats,
    activeChatId,
    moveChat,
    addChat: changeThread,
    overflowChatCount,
    setOverflowChatCount,
    errorBoundaryActive,
  } = useMiniChatStore();
  const currentOpenChat = chats.find(({ chatId }) => chatId === activeChatId);
  const { navigateToThread } = useInboxNavigate();
  const ref = useRef<HTMLSpanElement>(null);
  const containerWidthRef = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState(0);
  const { chatIdsOrder, threadIdsOrder: __ } = useMemo(
    () =>
      chats.reduce<{ chatIdsOrder: string[]; threadIdsOrder: string[] }>(
        (acc, { chatId, threadId }) => {
          acc.chatIdsOrder.push(chatId);
          acc.threadIdsOrder.push(threadId);
          return acc;
        },
        { chatIdsOrder: [], threadIdsOrder: [] }
      ),
    [JSON.stringify(chats)]
  );

  const { setNodeRef } = useDroppable({
    id: 'mini-chat-drag-and-drop',
  });
  const [chatIdBeingDragged, setChatIdBeingDragged] = useState<string>();
  const [containerSizing, setContainerSizing] = useState<{ width: number; right: number } | undefined>();

  useResizeObserver(containerWidthRef, (entry) => {
    const right = window.innerWidth - entry.target.getBoundingClientRect().right;
    setContainerSizing({
      width: entry.contentRect.width,
      right,
    });
  });

  useEffect(() => {
    const invalidChats = chats.filter((chat) => !locationIdsWithACL.includes(chat.groupId));
    if (invalidChats.length) removeChats(invalidChats.map((chat) => chat.chatId));
  }, [JSON.stringify(locationIdsWithACL)]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setWidth(entry.contentRect.width * 0.9);
      });
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        resizeObserver.unobserve(ref.current);
      }
    };
  }, []);

  const calculateSizingChatCounts = () => {
    if (width === 0 || isNaN(width)) return;
    let availableWidth = width;

    // Calculate if current sizing fits
    const calculatedChatSizes = chats.map<{ chatId: string; sizeVariant: SizeVariant }>(({ chatId }) => ({
      chatId,
      sizeVariant: activeChatId === chatId ? 'full' : 'minimized',
    }));
    chats.forEach((chat, i) => {
      const sizeVariant = calculatedChatSizes.find(({ chatId }) => chatId === chat.chatId)?.sizeVariant;
      const chatWidth = WIDTHS[sizeVariant ?? 'minimized'];
      availableWidth -= chatWidth + (i === chats.length - 1 ? 0 : CHAT_GAP);
    });
    if (availableWidth >= 0) {
      setAvatarChatCount(0);
      setOverflowChatCount(0);
      return;
    }

    // Calculate which chats need to be resized
    let neededAvatars = 0;
    let newChatsWillFit = false;
    chats.forEach((chat) => {
      if (newChatsWillFit) return;
      if (chat.chatId === activeChatId) return;
      const currentWidth = activeChatId === chat.chatId ? WIDTHS['full'] : WIDTHS['minimized'];
      const newWidth = WIDTHS['avatar'];
      availableWidth += currentWidth - newWidth;
      neededAvatars++;

      if (availableWidth >= 0) newChatsWillFit = true;
    });
    setAvatarChatCount(neededAvatars);
    if (neededAvatars < chats.length - (activeChatId ? 1 : 0)) {
      setOverflowChatCount(0);
      return;
    }

    // Calculate how many chats will overflow
    let overflowAvailableWidth = width;
    const reversedChats = [...chats].reverse();
    reversedChats.forEach((chat, index) => {
      if (overflowAvailableWidth < 0) return;
      const sizeVariant =
        chat.chatId === activeChatId ? 'full' : reversedChats.length - index <= neededAvatars ? 'avatar' : 'minimized';
      const totalChatWidth = WIDTHS[sizeVariant] + CHAT_GAP;
      overflowAvailableWidth -= totalChatWidth;
      if (overflowAvailableWidth < 0) {
        const remainingChatCount = reversedChats.length - index;
        setOverflowChatCount(remainingChatCount + 1);
      } else if (index === reversedChats.length - 1) {
        setOverflowChatCount(0);
      }
    });
  };

  const chatBeingDraggedIndex = chats.findIndex((chat) => chat.chatId === chatIdBeingDragged);
  const chatBeingDragged = chatBeingDraggedIndex === -1 ? undefined : chats[chatBeingDraggedIndex];
  const [destinationOrder, setDestinationOrder] = useState<string[]>();

  const handleDragOver = useCallback<NonNullable<ComponentProps<typeof DndContext>['onDragOver']>>(
    (e) => {
      const { active, over } = e;
      if (!over?.id || !active?.id) return;
      const overId = String(over.id);
      const activeId = String(active?.id);
      const destinationIndex = chatIdsOrder.findIndex((chatId) => chatId === overId);
      if (destinationIndex === -1) return;
      const destinationOrder = getDestinationOrder(chats, activeId, destinationIndex).map(({ chatId }) => chatId);
      setDestinationOrder(destinationOrder);
    },
    [JSON.stringify(chatIdsOrder), setDestinationOrder]
  );

  const getSizeVariant = useCallback(
    (chatId: string, index?: number): SizeVariant => {
      const resolvedIndex = index ?? chatIdsOrder.findIndex((id) => id === chatId);
      if (chatId === activeChatId) return rolledUp ? 'minimized' : 'full';
      if (
        chatIsAvatarVariant(
          resolvedIndex,
          avatarChatCount,
          chatIdsOrder.findIndex((chatId) => chatId === activeChatId)
        )
      )
        return 'avatar';
      return 'minimized';
    },
    [avatarChatCount, activeChatId, rolledUp, JSON.stringify(chatIdsOrder), chatIdBeingDragged]
  );

  const getTransformationSize = useCallback(
    ({
      currentOrder,
      destinationOrder,
      chatId,
    }: {
      currentOrder: string[];
      destinationOrder?: string[];
      chatId: string;
    }) => {
      if (!destinationOrder) return 0;
      const originalIndex = currentOrder.indexOf(chatId);
      const destinationIndex = destinationOrder.indexOf(chatId);

      if (originalIndex === destinationIndex) return 0;

      const originalDistanceFromRight = currentOrder
        .slice(0, originalIndex)
        .reduce<number>((acc, curr, index) => acc + WIDTHS[getSizeVariant(curr, index)] + CHAT_GAP, 0);
      const destinationDistanceFromRight = destinationOrder
        .slice(0, destinationIndex)
        .reduce<number>((acc, curr) => acc + WIDTHS[getSizeVariant(curr, currentOrder.indexOf(curr))] + CHAT_GAP, 0);

      const difference = originalDistanceFromRight - destinationDistanceFromRight;
      return difference;
    },
    [getSizeVariant, chatIdBeingDragged]
  );

  const transformations: number[] = useMemo(
    () =>
      chatIdsOrder
        .slice(overflowChatCount)
        .map((chatId) => getTransformationSize({ chatId, currentOrder: chatIdsOrder, destinationOrder })),
    [JSON.stringify(chatIdsOrder), JSON.stringify(destinationOrder), getTransformationSize, overflowChatCount]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useResizeObserver(ref, () => {
    calculateSizingChatCounts();
  });

  useEffect(() => {
    calculateSizingChatCounts();
  }, [JSON.stringify(chatIdsOrder), activeChatId]);

  const handleDragEnd = useCallback<NonNullable<ComponentProps<typeof DndContext>['onDragEnd']>>(
    (e) => {
      setChatIdBeingDragged(undefined);
      setDestinationOrder(undefined);
      const { active, over } = e;

      if (active.id !== over?.id) {
        const destinationIndex = chats.findIndex((chat) => chat.chatId === over?.id);
        if (destinationIndex === -1) return;
        const activeIndex = chats.findIndex((chat) => chat.chatId === active.id);
        if (activeIndex === -1) return;
        moveChat(String(active.id), destinationIndex);
      }
    },
    [setChatIdBeingDragged, JSON.stringify(chats), moveChat]
  );

  const { data: locationPhonesData } = InboxLocationPhones.Hooks.useLocationPhones();
  const locationPhonesByLocation = useMemo<Record<string, string[]>>(() => {
    return Object.fromEntries(
      Object.entries(locationPhonesData.locationPhonesByLocation).map(([locationId, locationPhones]) => [
        locationId,
        locationPhones.map(({ locationPhone }) => locationPhone),
      ])
    );
  }, [locationPhonesData.locationPhonesByLocation]);

  const currentChatLocationPhones = locationPhonesByLocation[currentOpenChat?.groupId ?? ''] ?? [];
  const currentChatCanChangeThreads =
    (currentOpenChat?.possiblePersonPhones.length ?? 0) > 1 || currentChatLocationPhones.length > 1;
  const v4ThreadStatusQuery = SMSDataV4.Queries.useBatchGetThreadStatusQuery(
    {
      requests: SMSDataV4.Utils.calculatePossibleThreadStatusRequests({
        personPhones: currentOpenChat?.possiblePersonPhones ?? [],
        locationPhonesByLocation,
      }),
    },
    {
      enabled: currentChatCanChangeThreads,
    }
  );

  const visibleChats = useMemo(() => chats.slice(overflowChatCount), [overflowChatCount, JSON.stringify(chats)]);

  return (
    <DndContext
      onDragStart={(e) => {
        setChatIdBeingDragged(String(e.active.id));
      }}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      collisionDetection={closestCenter}
      sensors={sensors}
    >
      <SortableContext items={chats.map((chat) => chat.chatId)} strategy={horizontalListSortingStrategy}>
        <span ref={containerWidthRef} css={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 0 }} />
        <span
          ref={ref}
          css={{
            position: 'fixed',
            bottom: 0,
            height: 0,
            right: containerSizing?.right ?? INITIAL_RIGHT,
            width: containerSizing?.width ?? 0,
            overflowY: 'visible',
            overflowX: 'clip',
            zIndex: theme.zIndex.higher,
            '::before': {
              position: 'absolute',
              content: '""',
              width: containerSizing?.width ?? 0,
              height: HEADER_HEIGHT,
              bottom: 0,
              pointerEvents: 'none',
              transition: 'opacity 300ms ease-in-out',
              opacity: rolledUp ? 0 : 1,
              background: chats.length ? `linear-gradient(transparent, ${theme.colors.white})` : 'transparent',
            },
          }}
        >
          <motion.span
            ref={setNodeRef}
            animate={{
              width: rolledUp ? WIDTHS.minimized : '100%',
              right: rolledUp ? `-${WIDTHS.minimized}px` : 0,
              gap: rolledUp ? 0 : theme.spacing(2),
              visibility: rolledUp ? 'hidden' : 'visible',
            }}
            transition={{
              visibility: {
                delay: rolledUp ? 1 : 0,
              },
            }}
            css={{
              position: 'absolute',
              bottom: 0,
              display: 'flex',
              flexDirection: 'row-reverse',
              alignItems: 'end',
              gap: theme.spacing(2),
              padding: theme.spacing(0, 2),
              height: chatIdBeingDragged ? '100vh' : 0,
              overflow: 'visible',
            }}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            {!errorBoundaryActive && (
              <AnimatePresence>
                {!!overflowChatCount && <OverflowChatsButton />}
                {visibleChats.map(({ chatId, possiblePersonPhones, ...chatContext }, index) => (
                  <MiniChat
                    key={chatId}
                    chatId={chatId}
                    {...chatContext}
                    sizeVariant={getSizeVariant(chatId, index)}
                    onClick={() => {
                      if (activeChatId !== chatId) {
                        openChat(chatId);
                      }
                    }}
                    onMaximize={() => {
                      navigateToThread({
                        threadId: chatContext.threadId,
                        groupId: chatContext.groupId,
                        personId: chatContext.contextPersonId,
                        locationPhone: chatContext.locationPhone,
                        personPhone: chatContext.personPhone,
                        threadItemId: chatContext.focusThreadItemData?.id,
                        threadItemCreatedAt: chatContext.focusThreadItemData?.createdAt,
                      });
                      removeChat(chatId);
                    }}
                    onMinimize={() => minimizeChat()}
                    onClose={() => removeChat(chatId)}
                    onSelectPersonPhone={(personPhone) => {
                      if (!currentOpenChat) return;

                      const newThread = v4ThreadStatusQuery.data?.threadStatuses.find(
                        (response) =>
                          response.personPhone === formatPhoneNumberE164(personPhone) &&
                          response.locationId === currentOpenChat.groupId &&
                          (currentOpenChat.locationPhone
                            ? response.locationPhone === currentOpenChat.locationPhone
                            : true)
                      );
                      if (!newThread) {
                        alert.error(t('Error switching to conversation.'));
                        sentry.error({
                          error: 'Error finding selected thread from thread status query response',
                          topic: 'messages',
                          team: 'fe-messaging',
                          addContext: {
                            name: 'Thread selection data',
                            context: {
                              threadStatusQuery: v4ThreadStatusQuery,
                              currentOpenChat,
                              newPersonPhone: personPhone,
                            },
                          },
                        });
                        return;
                      }

                      changeThread({
                        personPhone,
                        groupId: currentOpenChat.groupId,
                        contextPersonId: currentOpenChat.contextPersonId,
                        locationPhone: currentOpenChat.locationPhone,
                        unreadCount: 0,
                        threadId: newThread.threadId,
                        isNew: newThread.status === ThreadStatus.NEW,
                      });
                    }}
                    onSelectLocationPhone={({ locationPhone, locationId }, threadId) => {
                      if (!currentOpenChat) return;
                      const newThread = v4ThreadStatusQuery.data?.threadStatuses.find((response) =>
                        threadId
                          ? response.threadId === threadId
                          : response.personPhone === formatPhoneNumberE164(currentOpenChat.personPhone) &&
                            formatPhoneNumberE164(response.locationPhone) ===
                              formatPhoneNumberE164(locationPhone ?? '') &&
                            response.locationId === locationId
                      );

                      if (!newThread) {
                        alert.error(t('Error switching to conversation.'));
                        sentry.error({
                          error: 'Error finding selected thread from thread status query response',
                          topic: 'messages',
                          team: 'fe-messaging',
                          addContext: {
                            name: 'Thread selection data',
                            context: {
                              threadStatusQuery: v4ThreadStatusQuery,
                              currentOpenChat,
                              newLocationPhone: locationPhone,
                              locationPhone,
                            },
                          },
                        });
                        return;
                      }
                      changeThread({
                        personPhone: currentOpenChat.personPhone,
                        groupId: newThread.locationId,
                        contextPersonId: currentOpenChat.contextPersonId,
                        locationPhone: locationPhone || newThread.locationPhone,
                        unreadCount: 0,
                        threadId: newThread.threadId,
                        isNew: newThread.status === ThreadStatus.NEW,
                      });
                    }}
                    layout='position'
                    style={{
                      transform: `translateX(${transformations[index]}px)`,
                      transition: chatBeingDragged ? 'transform 0.3s ease-in-out' : undefined,
                    }}
                    css={[
                      {
                        transition: 'opacity 300ms ease-in-out',
                        '*': {
                          transition: 'opacity 300ms ease-in-out',
                        },
                      },
                      !!chatIdBeingDragged && { opacity: 0.8, '*': { opacity: 0.8 } },
                    ]}
                    dragIsActive={!!chatIdBeingDragged}
                    locationPhone={chatContext.locationPhone}
                  />
                ))}
              </AnimatePresence>
            )}
          </motion.span>
        </span>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {!!chatBeingDragged && (
          <OverlayChat {...chatBeingDragged} sizeVariant={getSizeVariant(chatBeingDragged.chatId)} />
        )}
      </DragOverlay>
    </DndContext>
  );
};

const OverlayChat = (props: Omit<MiniChatContext, 'sizeVariant'> & { sizeVariant: SizeVariant }) => {
  const { possiblePersonPhones, sizeVariant, focusThreadItemData, ...propsToUse } = props;

  return (
    <MiniChatHeader
      {...propsToUse}
      key={propsToUse.chatId + '-dragging'}
      onMaximize={() => {}}
      onMinimize={() => {}}
      onClose={() => {}}
      onSelectPersonPhone={() => {}}
      sizeVariant={sizeVariant}
      css={{
        borderRadius: theme.borderRadius.medium,
      }}
      initial={sizeVariant}
      floatingShadow
      hideButtons
      hideHoverLabel
      isSelectingContextContact={false}
      setIsSelectingContextContact={() => {}}
    />
  );
};
